// The card editor (spec §13.3): a panel beside the card for its title, note,
// checklist, assignees, due date and colour. Every change becomes an op as
// soon as it's made (text fields on Enter or when they lose focus), so others
// see it live; their changes update the panel without disturbing the field
// being typed in.
import type { Card, ChecklistItem, Op } from '../../shared/types.ts';
import { CARD_COLORS, CARD_LIMITS, isValidDue, sortedItems } from '../../shared/cards.ts';
import { keyBetween } from '../../shared/fractional-index.ts';
import { newId } from '../../shared/ids.ts';
import { fromPlainText, isEmptyRichText, plainText, truncateRichText } from '../../shared/rich-text.ts';
import { peerColor } from '../../shared/protocol.ts';
import type { Editor } from '../editor.ts';
import { h, isEditableTarget } from '../util/dom.ts';
import { aabb } from '../util/geometry.ts';
import { initials } from './face.ts';

export interface CardEditOptions {
  /** The caller opened a history gesture (card creation) that closing the editor ends. */
  gesture?: boolean;
  /** The card was just created; it's discarded if left completely empty. */
  isNew?: boolean;
}

interface Session {
  id: string;
  gesture: boolean;
  isNew: boolean;
  unsubscribe: () => void;
}

export const CARD_COLOR_NAMES: Record<string, string> = {
  '#4262FF': 'Blue', '#30A46C': 'Green', '#F76B15': 'Orange', '#E5484D': 'Red',
  '#8E4EC6': 'Purple', '#D6A100': 'Yellow', '#12A594': 'Teal', '#5F6368': 'Grey',
};

const GAP = 12;          // between the card and the panel, screen px
const TOP_MIN = 64;      // clear of the board bar
const PICKER_FILTER_AT = 8;

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

const personIcon = '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="7" r="3"/><path d="M2.5 16.5a5.5 5.5 0 0 1 11 0M15.5 6v5M13 8.5h5"/></svg>';

export class CardEditor {
  private readonly ed: Editor;
  private session: Session | null = null;
  private readonly panel: HTMLDivElement;
  private readonly colors: HTMLDivElement;
  private readonly title: HTMLTextAreaElement;
  private readonly note: HTMLTextAreaElement;
  private readonly assignees: HTMLDivElement;
  private readonly due: HTMLInputElement;
  private readonly dueClear: HTMLButtonElement;
  private readonly progress: HTMLSpanElement;
  private readonly bar: HTMLDivElement;
  private readonly items: HTMLUListElement;
  private readonly addItem: HTMLInputElement;
  private picker: HTMLDivElement | null = null;
  private pickerAnchor: HTMLElement | null = null;
  private dragging = false;

  constructor(editor: Editor) {
    this.ed = editor;
    const close = h('button', { type: 'button', class: 'tb-btn', 'aria-label': 'Close card', title: 'Close (Esc)' }, ['×']);
    this.colors = h('div', { class: 'cb-group ce-colors', role: 'group', 'aria-label': 'Colour' }, CARD_COLORS.map((c) => {
      const name = CARD_COLOR_NAMES[c] ?? c;
      const b = h('button', { type: 'button', class: 'swatch', title: name, 'aria-label': `Colour: ${name}`, 'aria-pressed': 'false' });
      b.style.background = c;
      b.dataset.color = c;
      b.addEventListener('click', () => {
        this.ed.cardColor = c;
        const card = this.card();
        if (card && card.color !== c) this.edit([{ t: 'update', id: card.id, props: { color: c } }]);
      });
      return b;
    }));
    this.title = h('textarea', { class: 'ce-title', rows: '1', maxlength: String(CARD_LIMITS.titleLength), placeholder: 'Card title', 'aria-label': 'Title', spellcheck: 'true' });
    this.note = h('textarea', { class: 'ce-note', rows: '2', placeholder: 'Add a note…', 'aria-label': 'Note', spellcheck: 'true' });
    this.assignees = h('div', { class: 'ce-people', role: 'group', 'aria-label': 'Assigned' });
    this.due = h('input', { type: 'date', class: 'ce-due', 'aria-label': 'Due date' });
    this.dueClear = h('button', { type: 'button', class: 'tb-btn small', 'aria-label': 'Clear due date', title: 'Clear due date' }, ['×']);
    this.progress = h('span', { class: 'ce-progress muted' });
    this.bar = h('div', { class: 'ce-bar', 'aria-hidden': 'true' }, [h('div')]);
    this.items = h('ul', { class: 'ce-items', 'aria-label': 'Checklist' });
    this.addItem = h('input', { type: 'text', class: 'ce-add', maxlength: String(CARD_LIMITS.itemLength), placeholder: 'Add an item', 'aria-label': 'Add a checklist item' });

    this.panel = h('div', { class: 'panel card-editor', role: 'dialog', 'aria-label': 'Card', hidden: '' }, [
      h('div', { class: 'ce-head' }, [this.colors, close]),
      this.title,
      this.note,
      h('div', { class: 'ce-meta' }, [
        // On a board kept in the browser there's nobody to assign.
        ...(editor.local ? [] : [h('span', { class: 'ce-label' }, ['Assigned']), this.assignees]),
        h('span', { class: 'ce-label' }, ['Due']), h('div', { class: 'ce-due-row' }, [this.due, this.dueClear]),
      ]),
      h('div', { class: 'ce-list-head' }, [h('span', { class: 'ce-label' }, ['Checklist']), this.progress]),
      this.bar,
      this.items,
      this.addItem,
    ]);
    document.body.append(this.panel);

    close.addEventListener('click', () => this.close());
    // Keys typed here belong to the panel, not the board's shortcuts.
    this.panel.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key !== 'Escape' || e.isComposing) return;
      e.preventDefault();
      if (this.picker) this.closePicker(true);
      else this.close();
    });
    for (const ta of [this.title, this.note]) ta.addEventListener('input', () => autosize(ta));
    this.title.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      e.preventDefault();
      this.commitTitle();
      this.addItem.focus();
    });
    this.title.addEventListener('blur', () => this.commitTitle());
    this.note.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.note.blur(); }
    });
    this.note.addEventListener('blur', () => this.commitNote());
    this.due.addEventListener('change', () => this.setDue(this.due.value));
    this.dueClear.addEventListener('click', () => this.setDue(''));
    this.addItem.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      e.preventDefault();
      this.appendItem();
    });
    // Outside the panel (the board included), a press closes it first.
    document.addEventListener('pointerdown', (e) => {
      const t = e.target as Node;
      if (this.picker?.contains(t)) return;
      if (this.picker) this.closePicker(false);
      if (this.session && !this.panel.contains(t)) this.close();
    }, true);
    // Esc with focus outside the panel (e.g. after clicking its background) still closes it, not the board's selection.
    document.addEventListener('keydown', (e) => {
      const t = e.target as Node;
      if (!this.session || e.key !== 'Escape' || this.panel.contains(t) || this.picker?.contains(t) || isEditableTarget(t)) return;
      e.preventDefault();
      e.stopPropagation();
      if (this.picker) this.closePicker(true);
      else this.close();
    }, true);
    window.addEventListener('resize', () => this.position());
  }

  get editingId(): string | null {
    return this.session?.id ?? null;
  }

  open(id: string, opts: CardEditOptions = {}): void {
    if (this.session?.id === id) return;
    this.close();
    const ed = this.ed;
    this.session = {
      id, gesture: !!opts.gesture, isNew: !!opts.isNew,
      unsubscribe: combine(
        ed.events.on('camera', () => this.position()),
        ed.store.subscribe((ids) => { if (ids.has(id)) this.render(); }),
      ),
    };
    ed.people.refresh?.();
    this.title.value = '';
    this.note.value = '';
    this.addItem.value = '';
    this.items.replaceChildren();
    this.panel.hidden = false;
    this.render();
    this.title.focus({ preventScroll: true });
    this.title.setSelectionRange(this.title.value.length, this.title.value.length);
  }

  /** Commits whatever is being typed and closes. */
  close(): void {
    const s = this.session;
    if (!s) return;
    // Blurring commits the focused field.
    if (this.panel.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.pendingAdd();
    this.teardown();
    const ed = this.ed;
    const card = ed.cardOf(s.id);
    if (s.isNew && card && isEmptyCard(card)) {
      if (s.gesture) ed.cancelGesture();
      else ed.apply([{ t: 'delete', id: s.id }]);
      return;
    }
    if (s.gesture) ed.endGesture();
  }

  private teardown(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.unsubscribe();
    this.closePicker(false);
    this.panel.hidden = true;
    this.ed.renderer.invalidateInteractive();
  }

  private card(): Card | null {
    return this.session ? this.ed.cardOf(this.session.id) : null;
  }

  private edit(ops: Op[]): void {
    if (this.session) this.ed.editCard(this.session.id, ops);
  }

  // --- rendering (also after changes by others) ---

  private render(): void {
    const card = this.card();
    if (!card) {
      // Deleted (by someone else, or undone): nothing left to edit.
      const s = this.session;
      this.teardown();
      if (s?.gesture) this.ed.endGesture();
      return;
    }
    const focused = document.activeElement;
    for (const b of this.colors.children as HTMLCollectionOf<HTMLElement>) b.setAttribute('aria-pressed', String(b.dataset.color === card.color));
    if (focused !== this.title) setValue(this.title, card.title);
    if (focused !== this.note) setValue(this.note, plainText(card.note));
    this.renderAssignees(card);
    if (focused !== this.due) this.due.value = card.due ?? '';
    this.dueClear.hidden = !card.due;
    this.renderItems(card);
    const all = Object.values(card.items);
    const done = all.filter((i) => i.done).length;
    this.progress.textContent = all.length ? `${done}/${all.length}` : '';
    (this.bar.firstChild as HTMLElement).style.width = all.length ? `${(done / all.length) * 100}%` : '0';
    this.bar.hidden = !all.length;
    const full = all.length >= CARD_LIMITS.items;
    this.addItem.disabled = full;
    this.addItem.placeholder = full ? `A card holds at most ${CARD_LIMITS.items} items` : 'Add an item';
    this.position(); // now, not next frame: the panel must never show up in the wrong place
  }

  private renderAssignees(card: Card): void {
    const people = this.ed.people;
    const chips = card.assignees.map((uid) => {
      const name = people.name(uid);
      const label = name ?? 'Former member';
      const b = h('button', { type: 'button', class: 'avatar ce-avatar', title: `${label} (click to unassign)`, 'aria-label': `Unassign ${label}` },
        [name ? initials(name) : '']);
      b.style.background = name ? peerColor(uid) : '#C9CDD4';
      b.addEventListener('click', () => this.setAssignees(card.assignees.filter((a) => a !== uid)));
      return b;
    });
    const add = h('button', { type: 'button', class: 'tb-btn small ce-assign', 'aria-label': 'Assign people', title: 'Assign people' });
    add.innerHTML = personIcon;
    add.addEventListener('click', () => this.openPicker(add, {
      selected: (uid) => !!this.card()?.assignees.includes(uid),
      multi: true,
      pick: (uid) => {
        const cur = this.card()?.assignees ?? [];
        if (!uid) return;
        if (cur.includes(uid)) this.setAssignees(cur.filter((a) => a !== uid));
        else if (cur.length < CARD_LIMITS.assignees) this.setAssignees([...cur, uid]);
      },
    }));
    this.assignees.replaceChildren(...chips, add);
  }

  private renderItems(card: Card): void {
    if (this.dragging) return;
    const rows = new Map<string, HTMLLIElement>();
    for (const li of [...this.items.children] as HTMLLIElement[]) {
      const id = li.dataset.id!;
      if (Object.hasOwn(card.items, id)) rows.set(id, li);
      else li.remove();
    }
    sortedItems(card).forEach(([itemId, item], i) => {
      const existing = rows.get(itemId);
      const li = existing ?? this.itemRow(itemId);
      if (this.items.children[i] !== li) this.items.insertBefore(li, this.items.children[i] ?? null);
      this.updateRow(li, item);
      // New rows can only be measured once they're in the document.
      if (!existing) autosize(li.querySelector('textarea')!);
    });
  }

  private itemRow(itemId: string): HTMLLIElement {
    const handle = h('span', { class: 'ce-handle', title: 'Drag to reorder', 'aria-hidden': 'true' }, ['⋮⋮']);
    const check = h('input', { type: 'checkbox', class: 'ce-check', 'aria-label': 'Done' });
    const text = h('textarea', { class: 'ce-item-text', rows: '1', maxlength: String(CARD_LIMITS.itemLength), 'aria-label': 'Item', spellcheck: 'true' });
    const person = h('button', { type: 'button', class: 'ce-item-person', 'aria-label': 'Assign this item' });
    person.hidden = this.ed.local;
    const remove = h('button', { type: 'button', class: 'tb-btn small ce-remove', 'aria-label': 'Remove item', title: 'Remove item' }, ['×']);
    const li = h('li', { class: 'ce-item' }, [handle, check, text, person, remove]);
    li.dataset.id = itemId;
    const cardId = this.session!.id;
    const op = (props: Partial<ChecklistItem> | null): Op => ({ t: 'item', id: cardId, itemId, props });

    check.addEventListener('change', () => this.edit([op({ done: check.checked })]));
    text.addEventListener('input', () => autosize(text));
    const commit = () => {
      const item = this.card()?.items[itemId];
      if (!item) return;
      const value = oneLine(text.value);
      if (!value) this.edit([op(null)]);
      else if (value !== item.text) this.edit([op({ text: value })]);
    };
    text.addEventListener('blur', commit);
    text.addEventListener('keydown', (e) => {
      if (e.isComposing) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        this.addItem.focus();
      } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        commit();
        this.shiftItem(itemId, e.key === 'ArrowUp' ? -1 : 1);
        text.focus();
      }
    });
    person.addEventListener('click', () => this.openPicker(person, {
      selected: (uid) => this.card()?.items[itemId]?.assignee === uid,
      multi: false,
      pick: (uid) => this.edit([op({ assignee: uid ?? undefined })]),
    }));
    remove.addEventListener('click', () => this.edit([op(null)]));
    handle.addEventListener('pointerdown', (e) => this.dragItem(e, li, itemId));
    return li;
  }

  private updateRow(li: HTMLLIElement, item: ChecklistItem): void {
    const [, check, text, person] = li.children as unknown as [HTMLElement, HTMLInputElement, HTMLTextAreaElement, HTMLButtonElement];
    check.checked = item.done;
    li.classList.toggle('done', item.done);
    if (document.activeElement !== text) setValue(text, item.text);
    const uid = item.assignee;
    const name = uid ? this.ed.people.name(uid) : null;
    person.classList.toggle('assigned', !!uid);
    person.title = uid ? `${name ?? 'Former member'} (click to change)` : 'Assign this item';
    if (uid) {
      person.textContent = name ? initials(name) : '';
      person.style.background = name ? peerColor(uid) : '#C9CDD4';
    } else {
      person.innerHTML = personIcon;
      person.style.background = '';
    }
  }

  // --- edits ---

  private commitTitle(): void {
    const card = this.card();
    const title = oneLine(this.title.value).slice(0, CARD_LIMITS.titleLength);
    if (card && title !== card.title) this.edit([{ t: 'update', id: card.id, props: { title } }]);
  }

  private commitNote(): void {
    const card = this.card();
    if (!card) return;
    const value = this.note.value.replace(/\s+$/, '');
    if (value === plainText(card.note).replace(/\s+$/, '')) return;
    const note = value.trim() ? truncateRichText(fromPlainText(value)) : undefined;
    if (note === undefined && isEmptyRichText(card.note)) return;
    this.edit([{ t: 'update', id: card.id, props: { note } }]);
  }

  private setDue(value: string): void {
    const card = this.card();
    if (!card) return;
    const due = value && isValidDue(value) ? value : undefined;
    if (due !== card.due) this.edit([{ t: 'update', id: card.id, props: { due } }]);
  }

  private setAssignees(assignees: string[]): void {
    const card = this.card();
    if (card) this.edit([{ t: 'update', id: card.id, props: { assignees } }]);
  }

  /** Adds what's typed in "Add an item" to the end of the checklist. */
  private appendItem(): void {
    const card = this.card();
    const text = oneLine(this.addItem.value).slice(0, CARD_LIMITS.itemLength);
    if (!card || !text || Object.keys(card.items).length >= CARD_LIMITS.items) return;
    const last = sortedItems(card).at(-1)?.[1].order ?? null;
    this.addItem.value = '';
    this.edit([{ t: 'item', id: card.id, itemId: newId(), props: { text, done: false, order: keyBetween(last, null) } }]);
  }

  /** Closing with something typed in "Add an item" keeps it. */
  private pendingAdd(): void {
    if (oneLine(this.addItem.value)) this.appendItem();
  }

  /** Moves an item to position `index` among the others. */
  private moveItem(itemId: string, index: number): void {
    const card = this.card();
    if (!card || !Object.hasOwn(card.items, itemId)) return;
    const all = sortedItems(card);
    if (all.findIndex(([id]) => id === itemId) === index) return;
    const others = all.filter(([id]) => id !== itemId);
    const lower = others[index - 1]?.[1].order ?? null;
    // Concurrent inserts can leave equal keys: aim between distinct neighbours.
    const upper = others.slice(index).map(([, it]) => it.order).find((o) => lower === null || o > lower) ?? null;
    this.edit([{ t: 'item', id: card.id, itemId, props: { order: keyBetween(lower, upper) } }]);
  }

  private shiftItem(itemId: string, delta: -1 | 1): void {
    const card = this.card();
    if (!card) return;
    const i = sortedItems(card).findIndex(([id]) => id === itemId);
    const n = Object.keys(card.items).length;
    const to = i + delta;
    if (i >= 0 && to >= 0 && to < n) this.moveItem(itemId, to);
  }

  /** Drag an item by its handle to reorder. */
  private dragItem(e: PointerEvent, li: HTMLLIElement, itemId: string): void {
    e.preventDefault();
    const handle = e.currentTarget as HTMLElement;
    handle.setPointerCapture(e.pointerId);
    this.dragging = true;
    li.classList.add('dragging');
    const startY = e.clientY;
    const target = (y: number) => {
      let index = 0;
      for (const other of this.items.children) {
        if (other === li) continue;
        const r = other.getBoundingClientRect();
        if (y > r.top + r.height / 2) index++;
      }
      return index;
    };
    const move = (ev: PointerEvent) => { li.style.transform = `translateY(${ev.clientY - startY}px)`; };
    const end = (ev: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      li.classList.remove('dragging');
      li.style.transform = '';
      this.dragging = false;
      if (ev.type === 'pointerup') this.moveItem(itemId, target(ev.clientY));
      const card = this.card();
      if (card) this.renderItems(card);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  // --- people picker ---

  private openPicker(anchor: HTMLElement, opts: { selected: (uid: string) => boolean; multi: boolean; pick: (uid: string | null) => void }): void {
    this.closePicker(false);
    const people = this.ed.people.list;
    const list = h('div', { class: 'ce-picker-list', role: 'listbox', 'aria-label': 'People', 'aria-multiselectable': String(opts.multi) });
    const filter = h('input', { type: 'text', class: 'ce-picker-filter', placeholder: 'Find someone', 'aria-label': 'Find someone' });
    const picker = h('div', { class: 'panel menu ce-picker' }, people.length > PICKER_FILTER_AT ? [filter, list] : [list]);
    const fill = () => {
      // Re-rendering must not drop keyboard focus (Esc would then go to the board).
      const focusedUid = list.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.uid : undefined;
      const q = filter.value.trim().toLowerCase();
      const rows: HTMLElement[] = [];
      if (!opts.multi) rows.push(option(null, 'Unassigned', !people.some((p) => opts.selected(p.id))));
      for (const p of people) if (!q || p.name.toLowerCase().includes(q)) rows.push(option(p.id, p.name, opts.selected(p.id)));
      if (!people.length) rows.push(h('p', { class: 'muted ce-picker-empty' }, ['Nobody to assign yet.']));
      list.replaceChildren(...rows);
      if (focusedUid !== undefined) (rows.find((r) => r.dataset.uid === focusedUid) as HTMLElement | undefined)?.focus();
    };
    const option = (uid: string | null, name: string, on: boolean) => {
      const dot = h('span', { class: 'avatar ce-avatar' }, [uid ? initials(name) : '']);
      dot.style.background = uid ? peerColor(uid) : 'transparent';
      const b = h('button', { type: 'button', class: 'menu-item ce-option', role: 'option', 'aria-selected': String(on) },
        [dot, h('span', {}, [name]), h('span', { class: 'ce-tick', 'aria-hidden': 'true' }, [on ? '✓' : ''])]);
      b.dataset.uid = uid ?? '';
      b.addEventListener('click', () => {
        opts.pick(uid);
        if (opts.multi) fill();
        else this.closePicker(true);
      });
      return b;
    };
    filter.addEventListener('input', fill);
    fill();
    document.body.append(picker);
    const r = anchor.getBoundingClientRect();
    const pw = picker.offsetWidth, ph = picker.offsetHeight;
    const x = Math.min(Math.max(8, r.left), innerWidth - pw - 8);
    const y = r.bottom + 4 + ph <= innerHeight - 8 ? r.bottom + 4 : Math.max(8, r.top - 4 - ph);
    picker.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    this.picker = picker;
    picker.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); this.closePicker(true); }
    });
    ((people.length > PICKER_FILTER_AT ? filter : list.querySelector('button')) as HTMLElement | null)?.focus();
    this.pickerAnchor = anchor;
  }

  private closePicker(refocus: boolean): void {
    if (!this.picker) return;
    this.picker.remove();
    this.picker = null;
    if (refocus && this.pickerAnchor?.isConnected) this.pickerAnchor.focus();
    this.pickerAnchor = null;
  }

  // --- placement ---

  /** Beside the card (right, else left), following the camera. */
  private position(): void {
    const card = this.card();
    if (!card || this.panel.hidden) return;
    const cam = this.ed.camera;
    const r = aabb(card);
    const [left, top] = cam.worldToScreen(r.x, r.y);
    const [right] = cam.worldToScreen(r.x + r.w, r.y + r.h);
    const pw = this.panel.offsetWidth, ph = this.panel.offsetHeight;
    const vw = innerWidth, vh = innerHeight;
    let x = right + GAP;
    if (x + pw > vw - 8) x = left - GAP - pw;
    if (x < 8) x = vw - pw - 8;
    const y = Math.min(Math.max(TOP_MIN, top), Math.max(TOP_MIN, vh - ph - 8));
    this.panel.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }
}

function isEmptyCard(c: Card): boolean {
  return !c.title.trim() && isEmptyRichText(c.note) && !Object.keys(c.items).length && !c.assignees.length && !c.due;
}

function setValue(ta: HTMLTextAreaElement, value: string): void {
  if (ta.value === value) return;
  ta.value = value;
  autosize(ta);
}

/** Textareas grow with their content. */
function autosize(ta: HTMLTextAreaElement): void {
  ta.style.height = 'auto';
  ta.style.height = `${ta.scrollHeight}px`;
}

function combine(...fns: (() => void)[]): () => void {
  return () => { for (const f of fns) f(); };
}
